import type { ExpectedStats } from '@konyaxl/shared';
import { useEffect, useState } from 'react';
import { ApiRequestError, api } from '../core/api.js';
import { QrDialog } from './QrDialog.js';

interface Props {
  tenantSlug: string;
  onClose: () => void;
}

/**
 * Okutulmayan barkodlar listesi.
 *
 * Eski projede bu liste client'ta hesaplanıyordu: her ekran güncellemesinde
 * beklenen barkodların tamamı okunanlar listesiyle karşılaştırılıyordu
 * (rabiekle.dart:425-450). Burada sunucu hesaplayıp yalnızca kalanları
 * gönderiyor.
 */
export function RemainingPanel({ tenantSlug, onClose }: Props) {
  const [kodlar, setKodlar] = useState<string[]>([]);
  const [listeDisiKodlar, setListeDisiKodlar] = useState<string[]>([]);
  const [aktifListe, setAktifListe] = useState<'kalan' | 'listeDisi'>('kalan');
  const [stats, setStats] = useState<ExpectedStats | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [qrKod, setQrKod] = useState<string | null>(null);
  const [filtre, setFiltre] = useState('');

  useEffect(() => {
    let iptal = false;

    (async () => {
      try {
        const [s, r, u] = await Promise.all([
          api.get<ExpectedStats>(`/${tenantSlug}/expected/stats`),
          api.get<{ items: string[] }>(`/${tenantSlug}/expected/remaining?limit=2000`),
          api.get<{ items: string[] }>(`/${tenantSlug}/expected/unexpected?limit=2000`),
        ]);
        if (iptal) return;
        setStats(s);
        setKodlar(r.items);
        setListeDisiKodlar(u.items);
      } catch (err) {
        if (!iptal) {
          setHata(err instanceof ApiRequestError ? err.message : 'Liste yüklenemedi');
        }
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    })();

    return () => {
      iptal = true;
    };
  }, [tenantSlug]);

  const aktifKodlar = aktifListe === 'kalan' ? kodlar : listeDisiKodlar;
  const gosterilen = filtre.trim()
    ? aktifKodlar.filter((k) => k.toLowerCase().includes(filtre.trim().toLowerCase()))
    : aktifKodlar;

  return (
    <div className="panel-katman" onClick={onClose}>
      <aside className="kalan-panel" onClick={(e) => e.stopPropagation()}>
        <header className="panel-baslik">
          <h2>Okutulmayanlar</h2>
          <button className="btn-panel-kapat" onClick={onClose} aria-label="Kapat">
            ✕
          </button>
        </header>

        {stats && (
          <div className="panel-ozet">
            <span>
              <strong>{stats.remaining}</strong> kaldı
            </span>
            <span className="ayrac">·</span>
            <span>
              {stats.scanned} / {stats.total} okutuldu
            </span>
          </div>
        )}

        <div className="panel-sekmeler">
          <button
            className={aktifListe === 'kalan' ? 'aktif' : ''}
            onClick={() => setAktifListe('kalan')}
          >
            Okutulmayanlar ({kodlar.length})
          </button>
          <button
            className={aktifListe === 'listeDisi' ? 'aktif' : ''}
            onClick={() => setAktifListe('listeDisi')}
          >
            Listede olmayanlar ({listeDisiKodlar.length})
          </button>
        </div>

        {aktifKodlar.length > 20 && (
          <input
            className="panel-filtre"
            data-manual-input="true"
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder="Listede ara"
          />
        )}

        {hata && <div className="hata-kutu">{hata}</div>}

        {yukleniyor ? (
          <p className="panel-bilgi">Yükleniyor…</p>
        ) : gosterilen.length === 0 ? (
          <p className="panel-bilgi">
            {aktifKodlar.length === 0
              ? aktifListe === 'kalan'
                ? 'Tüm barkodlar okutuldu'
                : 'Bugün listede olmayan barkod okutulmadı'
              : 'Aramaya uyan kod yok'}
          </p>
        ) : (
          <>
            <p className="panel-ipucu">
              {aktifListe === 'kalan'
                ? 'Koda dokunun: panoya kopyalanır ve QR kodu açılır'
                : 'Bugün okutulan ama beklenen listede olmayan barkodlar'}
            </p>
            <ul className="kalan-liste">
              {gosterilen.map((kod, i) => (
                <li key={kod}>
                  <button className="kalan-satir" onClick={() => setQrKod(kod)}>
                    <span className="kalan-sira">{i + 1}</span>
                    <span className="kalan-kod">{kod}</span>
                    <span className="kalan-qr">⧉</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>

      {qrKod && (
        <QrDialog
          kodlar={[{ etiket: 'Barkod', deger: qrKod }]}
          onClose={() => setQrKod(null)}
        />
      )}
    </div>
  );
}
